import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import { LANGS, T } from './i18n';

const PHONE = '+34 689 755 102';
const PHONE_LINK = 'tel:+34689755102';
const WHATSAPP_LINK = 'https://wa.me/34689755102';
const EMAIL = 'hello@dhrutalabs.com';

// English service names are sent with the enquiry so emails read the same whatever the site language.
const SERVICE_VALUES = ['Website', 'Custom app', 'Redesign', 'AI integration', 'Not sure yet'];

const PROJECTS = [
  { key: 'ven', name: 'Venovio', live: true, meta: ['SaaS', 'Barcelona'], url: 'https://www.venovio.app', label: 'www.venovio.app', img: '/work/venovio.jpg' },
  { key: 'prep', name: 'PrepALTA', live: true, meta: ['EdTech', 'tag.ai'], url: 'https://prepalta.com', label: 'prepalta.com', img: '/work/prepalta.jpg' },
  { key: 'bt', name: 'BuildiTech', live: false, meta: ['tag.client', 'tag.website'], url: 'https://builditech.in', label: 'builditech.in', img: '/work/builditech.jpg' },
];

const storage = {
  get: (k) => { try { return window.localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* storage unavailable */ } },
};

const initialLang = () => {
  const saved = storage.get('dl-lang');
  if (LANGS.includes(saved)) return saved;
  const browser = (navigator.language || 'en').slice(0, 2);
  return LANGS.includes(browser) ? browser : 'en';
};

const Html = ({ as: Tag = 'span', html, ...rest }) => <Tag {...rest} dangerouslySetInnerHTML={{ __html: html }} />;

const Logo = ({ className = '' }) => (
  <a href="#top" className={`logo ${className}`} aria-label="Dhruta Labs">
    <img src="/brand/logo-primary@2x.png" alt="Dhruta Labs" width="124" height="30" />
  </a>
);

const WhatsAppIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
    <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.3-.5 0-1 .2-3.3-.7-2.8-1.1-4.5-4-4.7-4.2-.1-.2-1.1-1.5-1.1-2.8s.7-2 1-2.3c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.9 2.1c.1.2.1.4 0 .5l-.3.5-.4.5c-.1.1-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.3 2.4 1.5.3.1.5.1.6-.1l.9-1.1c.2-.3.4-.2.7-.1l2 1c.3.1.5.2.5.3.1.2.1.7-.1 1.2Z" />
  </svg>
);

// Pillar marks: motion trail (speed), crosshair (accuracy), steady signal (reliability).
const PillarIcon = ({ type }) => {
  const c = '#3DDC97';
  if (type === 1) {
    return (
      <svg width="56" height="40" viewBox="0 0 56 40" fill="none" aria-hidden="true">
        {[4, 12, 20, 28, 36].map((y, i) => (
          <rect key={y} x={i % 2 ? 12 : 4} y={y - 2} width={i % 2 ? 44 : 52} height="4" rx="1" fill={c} opacity={0.35 + i * 0.15} />
        ))}
      </svg>
    );
  }
  if (type === 2) {
    return (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke={c} strokeWidth="2" aria-hidden="true">
        <circle cx="20" cy="20" r="15" opacity=".5" />
        <circle cx="20" cy="20" r="7" />
        <path d="M20 0v10M20 30v10M0 20h10M30 20h10" strokeLinecap="round" />
        <circle cx="20" cy="20" r="2" fill={c} stroke="none" />
      </svg>
    );
  }
  return (
    <svg width="64" height="40" viewBox="0 0 64 40" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M0 22h16l5-12 7 22 6-16 4 6h26" />
    </svg>
  );
};

const App = () => {
  const [lang, setLangState] = useState(initialLang);
  const [hint, setHint] = useState(false);
  const [formStatus, setFormStatus] = useState('idle');
  const t = useCallback((key) => T[lang][key] ?? T.en[key] ?? key, [lang]);
  const list = (key) => t(key).split('|');

  useEffect(() => {
    document.documentElement.lang = lang;
    storage.set('dl-lang', lang);
  }, [lang]);

  // The page renders client-side, so honour a #section link after the first render.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  // Show the language hint once per visitor.
  useEffect(() => {
    if (storage.get('dl-lang-hint')) return undefined;
    const show = setTimeout(() => setHint(true), 1200);
    const hide = setTimeout(() => { setHint(false); storage.set('dl-lang-hint', '1'); }, 10000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, []);

  const dismissHint = () => { setHint(false); storage.set('dl-lang-hint', '1'); };
  const setLang = (l) => { setLangState(l); dismissHint(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    data.set('lang', lang);
    setFormStatus('sending');
    try {
      const res = await fetch('/api/contact', { method: 'POST', body: data });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      form.reset();
      setFormStatus('sent');
    } catch {
      setFormStatus('error');
    }
  };

  const tickerItems = list('tk');

  return (
    <div className="site" id="top">
      <nav className="nav">
        <div className="wrap">
          <Logo />
          <div className="nav-links">
            <a href="#approach">{t('nav.approach')}</a>
            <a href="#work">{t('nav.work')}</a>
            <a href="#services">{t('nav.services')}</a>
            <a href="#team">{t('nav.team')}</a>
            <a href="#contact">{t('nav.contact')}</a>
          </div>
          <div className="nav-right">
            <div className="lang-wrap">
              <div className={`lang ${hint ? 'pulse' : ''}`} role="group" aria-label="Language">
                {LANGS.map((l) => (
                  <button key={l} type="button" className={l === lang ? 'on' : ''} onClick={() => setLang(l)} aria-pressed={l === lang}>
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className={`lang-hint ${hint ? 'show' : ''}`} role="status">
                <button type="button" aria-label="Close" onClick={dismissHint}>×</button>
                <b>{t('hint.t')}</b>
                <span>{t('hint.d')}</span>
              </div>
            </div>
            <a href="#contact" className="btn btn-solid">{t('nav.cta')}</a>
          </div>
        </div>
      </nav>

      <header className="hero">
        <div className="hero-glow" />
        <div className="wrap">
          <div className="status reveal d1"><i />{t('hero.status')}</div>
          <Html as="h1" className="reveal d2" html={t('hero.title')} />
          <div className="hero-foot">
            <p className="reveal d3">{t('hero.lede')}</p>
            <div className="hero-cta reveal d4">
              <a href="#work" className="btn btn-ghost">{t('hero.cta1')}</a>
              <a href="#contact" className="btn btn-solid">{t('hero.cta2')}</a>
            </div>
          </div>
        </div>
      </header>

      <div className="ticker" aria-hidden="true">
        <div className="ticker-track">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className={i % tickerItems.length < 3 ? 'hl' : ''}>{item}</span>
          ))}
        </div>
      </div>

      <section id="approach" className="section">
        <div className="wrap">
          <div className="shead">
            <Html as="h2" html={t('ap.title')} />
            <p>{t('ap.sub')}</p>
          </div>
          <div className="pillars">
            {[1, 2, 3].map((n) => (
              <div className="pillar" key={n}>
                <PillarIcon type={n} />
                <h3>{t(`ap${n}.t`)}</h3>
                <p>{t(`ap${n}.d`)}</p>
                <ul>{list(`ap${n}.l`).map((li) => <li key={li}>{li}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="work" className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="shead">
            <Html as="h2" html={t('work.title')} />
            <p>{t('work.sub')}</p>
          </div>
          <div className="work">
            {PROJECTS.map((p) => (
              <article className="case" key={p.key}>
                <div className="case-info">
                  <div className="tags mono">
                    {p.live && <span className="live">{t('tag.live')}</span>}
                    {p.meta.map((m) => <span key={m}>{m.startsWith('tag.') ? t(m) : m}</span>)}
                  </div>
                  <h3>{p.name}</h3>
                  <p>{t(`${p.key}.desc`)}</p>
                  <div className="chips">{list(`${p.key}.chips`).map((c) => <span key={c}>{c}</span>)}</div>
                  <a className="visit" href={p.url} target="_blank" rel="noopener noreferrer">{p.label}</a>
                </div>
                <div className="case-visual">
                  <div className="browser">
                    <div className="browser-bar"><i /><i /><i /><span>{p.label}</span></div>
                    <img src={p.img} alt={`${p.name} website`} loading="lazy" width="1600" height="1000" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="services" className="section">
        <div className="wrap">
          <div className="shead">
            <Html as="h2" html={t('svc.title')} />
            <p>{t('svc.sub')}</p>
          </div>
          <div className="services">
            {[1, 2, 3, 4].map((n) => (
              <div className="svc" key={n}>
                <h3>{t(`s${n}.t`)}</h3>
                <p>{t(`s${n}.d`)}</p>
                <ul>{list(`s${n}.l`).map((li) => <li key={li}>{li}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="team" className="section team">
        <div className="wrap">
          <div className="shead"><Html as="h2" html={t('team.title')} /></div>
          <div className="team-grid">
            <div>
              <Html as="p" className="quote" html={t('team.quote')} />
              <div className="sig"><img src="/brand/avatar-dark.png" alt="" />{t('team.sig')}</div>
            </div>
            <div className="facts">
              <div className="fact"><b>30+</b><span>{t('f1')}</span></div>
              <div className="fact"><b>2</b><span>{t('f2')}</span></div>
              <div className="fact"><b>EN·ES·CA</b><span>{t('f3')}</span></div>
              <div className="fact"><b>100%</b><span>{t('f4')}</span></div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="section contact">
        <div className="wrap contact-grid">
          <div>
            <Html as="h2" html={t('c.title')} />
            <p className="sub">{t('c.sub')}</p>
            <div className="channels">
              <a className="ch wa" href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                <div><small>WhatsApp</small><b>{t('c.wa')}</b></div><span className="arr">→</span>
              </a>
              <a className="ch" href={PHONE_LINK}>
                <div><small>{t('c.phone')}</small><b>{PHONE}</b></div><span className="arr">→</span>
              </a>
              <a className="ch" href={`mailto:${EMAIL}`}>
                <div><small>{t('c.email')}</small><b>{EMAIL}</b></div><span className="arr">→</span>
              </a>
            </div>
          </div>

          <form className="form" onSubmit={handleSubmit}>
            <input className="hp" type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="two">
              <label className="field">{t('f.name')}<input name="name" required maxLength={120} placeholder={t('f.name.ph')} /></label>
              <label className="field">{t('f.email')}<input name="email" type="email" required maxLength={200} placeholder={t('f.email.ph')} /></label>
            </div>
            <div className="two">
              <label className="field">{t('f.phone')}<input name="phone" type="tel" required maxLength={40} autoComplete="tel" placeholder={t('f.phone.ph')} /></label>
              <label className="field">{t('f.company')}<input name="company" maxLength={200} placeholder={t('f.company.ph')} /></label>
            </div>
            <div className="field">
              {t('f.need')}
              <div className="pills">
                {list('f.needs').map((label, i) => (
                  <label key={SERVICE_VALUES[i]}>
                    <input type="checkbox" name="service" value={SERVICE_VALUES[i]} defaultChecked={i === 0} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="field">{t('f.msg')}<textarea name="message" required maxLength={5000} placeholder={t('f.msg.ph')} /></label>
            <button className="btn btn-solid" type="submit" disabled={formStatus === 'sending'}>
              {formStatus === 'sending' ? t('f.sending') : t('f.send')}
            </button>
            {formStatus === 'sent' && <p className="form-msg ok" role="status">{t('f.sent')}</p>}
            {formStatus === 'error' && <p className="form-msg err" role="alert">{t('f.error')}</p>}
          </form>
        </div>
      </section>

      <footer className="footer">
        <div className="wrap">
          <Logo />
          <span>{t('foot')}</span>
          <span>{EMAIL} · {PHONE}</span>
        </div>
      </footer>

      <a className="wa-float" href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
        <WhatsAppIcon />
      </a>
    </div>
  );
};

export default App;
